/* eslint-disable no-irregular-whitespace */
import Card from '@carvana/ds-card';
import { ChevronDown } from 'lucide-react';
import cx from 'classnames';
import React, { useState } from 'react';
import CardBrandIcon, { isKnownCardBrand } from '|/components/carvana-ds/card-brand-icon';
import { currency } from '|/helpers';
import ApplePayMark from '|/icons/apple-pay-mark.svg';
import GooglePayMark from '|/icons/google-pay-mark.svg';
import MicrOnUs from '|/icons/micr-on-us.svg';
import MicrTransit from '|/icons/micr-transit.svg';
import styles from './payment-breakdown.module.css';
import { usePaymentContext } from '../../../contexts/payment';
import type { PaymentSelection } from '../../../services/purchase-payments-api.types';
import {
  getDigitalWalletLabel,
  getPayLaterLabel,
  isPayLaterSelection
} from '../utils/digital-wallet';

const cashDownLabel = 'Cash down payment';
const subtotalLabel = 'Subtotal';
const shippingLabel = 'Nonrefundable shipping';
const creditLabel = 'Payments applied';
const amountDueNowLabel = 'Amount due now';
const amountDueLaterLabel = 'Amount due';
const refundDueLabel = 'Refund due';
const paymentMethodLabel = 'Payment method';

const subtotalSubtext =
  'Includes taxes, title, registration, upgrades, optional add\u2011ons and trade\u2011in';
const shippingFeeSubText = 'Transporting the vehicle to a Carvana location near you.';
const refundDueSubtext =
  'We\u2019ll send your refund to your original payment method or by check after your 7\u2011Day Money Back Guarantee expires.';

const PaymentMethodValue = ({
  paymentSelection
}: {
  paymentSelection: PaymentSelection | undefined;
}) => {
  switch (paymentSelection?.selectionType) {
    case 'BankAccount':
      if (!paymentSelection.bankAccount) {
        return null;
      }

      return (
        <span className={styles.value}>
          Account
          <MicrTransit width={9} height={12} aria-hidden="true" />
          {`\u2022\u2022\u2022\u2022${paymentSelection.bankAccount.accountMask}`}
          <MicrOnUs width={9} height={9} aria-hidden="true" />
        </span>
      );

    case 'Card': {
      if (!paymentSelection.card) {
        return null;
      }

      const { cardBrand, cardMask } = paymentSelection.card;

      return (
        <span className={styles.value}>
          <CardBrandIcon brand={cardBrand} />
          {!isKnownCardBrand(cardBrand) && 'Card '}
          {`\u2022\u2022\u2022\u2022 ${cardMask}`}
        </span>
      );
    }

    case 'DigitalWallet':
      switch (paymentSelection.digitalWallet) {
        case 'ApplePay':
          return (
            <span className={styles.value}>
              <ApplePayMark width="24px" height="16px" aria-hidden="true" />
              {getDigitalWalletLabel(paymentSelection.digitalWallet)}
            </span>
          );

        case 'GooglePay':
          return (
            <span className={styles.value}>
              <GooglePayMark width="24px" height="16px" aria-hidden="true" />
              {getDigitalWalletLabel(paymentSelection.digitalWallet)}
            </span>
          );

        default:
          return <span className={styles.value}>{getDigitalWalletLabel(undefined)}</span>;
      }

    case 'CashiersCheck':
    case 'WireTransfer':
      return <span className={styles.value}>{getPayLaterLabel(paymentSelection)}</span>;

    default:
      return null;
  }
};

const hasPaymentMethodValue = (paymentSelection: PaymentSelection | undefined) => {
  switch (paymentSelection?.selectionType) {
    case 'BankAccount':
      return !!paymentSelection.bankAccount;
    case 'Card':
      return !!paymentSelection.card;
    case 'DigitalWallet':
    case 'CashiersCheck':
    case 'WireTransfer':
      return true;
    default:
      return false;
  }
};

export const PaymentBreakdown = () => {
  const {
    state: {
      paymentSummary: {
        paymentType,
        purchasePaymentBreakdown: { cashDown, shippingFee, credit, amountDue, refundDue },
        paymentSelection
      }
    }
  } = usePaymentContext();

  const [isShippingDetailsCollapsed, setIsShippingDetailsCollapsed] = useState(true);
  const [isRefundDetailsCollapsed, setIsRefundDetailsCollapsed] = useState(true);

  const toggleShippingDetails = () => {
    setIsShippingDetailsCollapsed(value => !value);
  };

  const toggleRefundDetails = () => {
    setIsRefundDetailsCollapsed(value => !value);
  };

  const isCash = paymentType === 'Cash';
  const isPayLater = isPayLaterSelection(paymentSelection);
  const amountDueLabel = isPayLater ? amountDueLaterLabel : amountDueNowLabel;

  const shouldShowCashDown = !isCash && cashDown > 0;
  const shouldShowSubtotal = isCash && cashDown > 0 && shippingFee > 0;
  const shouldShowShippingFee = shippingFee > 0;
  const shouldShowCredit = credit > 0;
  const shouldShowAmountDue = refundDue === 0;
  const shouldShowRefundDue = refundDue > 0;

  const shouldShowPaymentMethod = amountDue > 0 && hasPaymentMethodValue(paymentSelection);

  const shouldShowLeadingDivider =
    shouldShowCashDown || shouldShowSubtotal || shouldShowShippingFee || shouldShowCredit;

  return (
    <>
      {shouldShowLeadingDivider && <Card.Divider />}

      <div className={styles.container}>
        {shouldShowCashDown && (
          <div className={styles.cashDown}>
            <div className={styles.lineItem}>
              <span className={styles.label}>{cashDownLabel}</span>
              <span className={styles.value}>{currency(cashDown).replace('.00', '')}</span>
            </div>
          </div>
        )}

        {shouldShowSubtotal && (
          <div className={styles.subtotal}>
            <div className={styles.lineItem}>
              <span className={styles.label}>{subtotalLabel}</span>
              <span className={styles.value}>{currency(cashDown).replace('.00', '')}</span>
            </div>

            <div className={styles.subtext}>{subtotalSubtext}</div>
          </div>
        )}

        {shouldShowShippingFee && (
          <div className={styles.shippingFee}>
            <div className={styles.lineItem}>
              <CollapsableButton
                label={shippingLabel}
                isCollapsed={isShippingDetailsCollapsed}
                onToggle={toggleShippingDetails}
                dataAnalyticsId="pp_complete_purchase_pao_toggle_shipping_fee_details"
              />
              <span className={styles.value}>{currency(shippingFee).replace('.00', '')}</span>
            </div>

            {!isShippingDetailsCollapsed && (
              <div className={styles.subtext}>{shippingFeeSubText}</div>
            )}
          </div>
        )}

        {shouldShowCredit && (
          <div className={styles.credit}>
            <div className={styles.lineItem}>
              <span className={styles.label}>{creditLabel}</span>
              <span className={styles.value}>-{currency(credit).replace('.00', '')}</span>
            </div>
          </div>
        )}

        <Card.Divider />

        {shouldShowAmountDue && (
          <div className={styles.amountDue}>
            <div className={styles.lineItem}>
              <span className={styles.label}>{amountDueLabel}</span>
              <span className={cx(styles.value, { [styles.valueSuccess]: amountDue === 0 })}>
                {currency(amountDue).replace('.00', '')}
              </span>
            </div>

            {shouldShowPaymentMethod && (
              <div className={styles.paymentMethod}>
                <span className={styles.label}>{paymentMethodLabel}</span>
                <PaymentMethodValue paymentSelection={paymentSelection} />
              </div>
            )}
          </div>
        )}

        {shouldShowRefundDue && (
          <div className={styles.refundDue}>
            <div className={styles.lineItem}>
              <CollapsableButton
                label={refundDueLabel}
                isCollapsed={isRefundDetailsCollapsed}
                onToggle={toggleRefundDetails}
                dataAnalyticsId="pp_complete_purchase_pao_toggle_refund_details"
              />
              <span className={styles.value}>{currency(refundDue).replace('.00', '')}</span>
            </div>

            {!isRefundDetailsCollapsed && <div className={styles.subtext}>{refundDueSubtext}</div>}
          </div>
        )}
      </div>
    </>
  );
};

const CollapsableButton = ({
  label,
  dataAnalyticsId,
  isCollapsed,
  onToggle
}: {
  label: string;
  dataAnalyticsId: string;
  isCollapsed: boolean;
  onToggle: () => void;
}) => {
  return (
    <button
      className={styles.toggleCollapseButton}
      onClick={onToggle}
      data-analytics-id={dataAnalyticsId}
    >
      <span className={styles.label}>{label}</span>
      <ChevronDown
        size={16}
        color="var(--icon-strong)"
        style={{ transform: isCollapsed ? undefined : 'rotate(180deg)' }}
      />
    </button>
  );
};
